import type { PriceAverageRow, PriceAverageMethod } from "@/lib/api/assistant";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  commodityLabel,
  formatNum,
  formatPct,
  formatWindow,
  humanizeKey,
  instrumentLabel,
} from "../utils";

const METHOD_LABEL: Record<PriceAverageMethod, string> = {
  avg_price: "Average Price",
  net_change: "Net Change",
  net_change_pct: "Net Change %",
  avg_bar_change: "Avg Bar-to-Bar Change",
};

function methodValue(method: PriceAverageMethod, raw: string): string {
  if (method === "net_change_pct") return formatPct(raw);
  return formatNum(raw);
}

function MethodCell({
  method,
  value,
  isPrimary,
}: {
  method: PriceAverageMethod;
  value: string;
  isPrimary: boolean;
}) {
  return (
    <div
      className={
        "rounded-md border p-3 " +
        (isPrimary ? "border-indigo-300 bg-indigo-50/60" : "border-slate-200 bg-white")
      }
    >
      <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center justify-between">
        <span>{METHOD_LABEL[method] ?? humanizeKey(method)}</span>
        {isPrimary && (
          <Badge variant="outline" className="border-indigo-300 text-indigo-700 bg-white text-[10px] px-1.5">
            primary
          </Badge>
        )}
      </div>
      <div className="text-xl font-semibold text-slate-900 mt-1 tabular-nums">
        {methodValue(method, value)}
      </div>
    </div>
  );
}

function Row({ row }: { row: PriceAverageRow }) {
  const methods = Object.keys(row.values) as PriceAverageMethod[];
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            {commodityLabel(row.commodity_id)}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{instrumentLabel(row.instrument)}</div>
        </div>
        <div className="text-xs text-slate-500 text-right">
          <div>{formatWindow(row.window)}</div>
          <div className="mt-0.5">{row.bar_count} bars</div>
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {methods.map((method) => (
          <MethodCell
            key={method}
            method={method}
            value={row.values[method] ?? ""}
            isPrimary={method === row.primary_method}
          />
        ))}
        {methods.length === 0 && (
          <div className="text-sm text-slate-500 italic col-span-full">No values returned.</div>
        )}
      </div>
    </div>
  );
}

export function PriceAverageResult({ rows }: { rows: PriceAverageRow[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Price Average</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 && (
          <div className="text-sm text-slate-500 italic">No rows returned.</div>
        )}
        {rows.map((row, i) => (
          <Row key={`${row.commodity_id}-${i}`} row={row} />
        ))}
      </CardContent>
    </Card>
  );
}
